import { useQuery } from "@tanstack/react-query";
import { Upload } from "lucide-react";
import React from "react";
import { UseFormReturn } from "react-hook-form";

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components";
import RichTextEditor from "@/components/rich-text-editor";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { CommissionModal } from "@/pages/platforms/components/commission-modal";
import { FontSelector } from "@/pages/platforms/components/font-selector";
import { PlatformColors } from "@/pages/platforms/components/platform-colors";
import { ProductsModal } from "@/pages/platforms/components/products-modal";
import { formatPrice } from "@/utils/monetary-formatter";

import { useClientPlans } from "../../clients/use-client-plans.hooks";
import * as projectsApi from "../../projects/projects.api";
import LandingPagePreview from "../landing-page/landing-page-preview";
import { IUsePlataform } from "../platform.hooks.interface";
import { TPlataformFormData } from "../platform.schema";

export interface IPlataformFormProps {
  open: boolean;
  form: UseFormReturn<TPlataformFormData>;
  onSubmit: (values: TPlataformFormData) => void;
  clientId: string;
  projectId?: string; // ID do projeto (opcional)
  isEditMode?: boolean;
  isLoading?: boolean;
  plataformHook: IUsePlataform;
}
export const PlataformForm: React.FC<IPlataformFormProps> = ({
  form,
  onSubmit,
  clientId,
  projectId,
  isEditMode = false,
  isLoading = false,
  plataformHook,
}) => {
  const { toast } = useToast();

  // Se tiver projectId, buscar planos do projeto, senão buscar planos do cliente
  const {
    clientPlans: plansFromClient,
    selectedPlan,
    isModalOpen,
    isProductsModalOpen,
    isLoadingPlan,
    openCommissionModal,
    closeCommissionModal,
    openProductsModal,
    closeProductsModal,
  } = useClientPlans(projectId ? "" : clientId || ""); // Só buscar planos do cliente se não tiver projectId

  // Buscar planos do projeto se projectId estiver disponível
  const { data: projectPlans, isLoading: isLoadingProjectPlans } = useQuery({
    queryKey: ["project-plans", projectId],
    queryFn: () => projectId ? projectsApi.getProjectPlans(projectId) : Promise.resolve([]),
    enabled: !!projectId,
    staleTime: 60_000,
  });

  // Usar planos do projeto se disponível, senão usar planos do cliente
  const clientPlans = projectId ? (projectPlans || []) : plansFromClient;
  const isLoadingPlans = projectId ? isLoadingProjectPlans : false;

  const {
    primaryImagePreview,
    setPrimaryImagePreview,
    primaryLogoPreview,
    setPrimaryLogoPreview,
    secondaryLogoPreview,
    setSecondaryLogoPreview,
    selectedPlans,
    togglePlanSelection,
    clearSelectedPlans,
    isPlanSelected,
    isEditMode: hookIsEditMode,
    editingPlatform,
  } = plataformHook;

  React.useEffect(() => {
    const planIds = selectedPlans.map((plan) => plan.id);
    form.setValue("planos", planIds);
  }, [selectedPlans, form]);


  const getSelectedPlansCount = () => {
    if (hookIsEditMode && editingPlatform) {
      // No modo de edição, contar quantos planos da plataforma estão nos planos disponíveis
      return clientPlans.filter(plan => 
        editingPlatform.plans.some(platformPlan => platformPlan.id === plan.id)
      ).length;
    }
    return selectedPlans.length;
  };

  return (
    <div className="space-y-4">
      <Tabs defaultValue="tab1">
        <TabsList>
          <TabsTrigger value="tab1">Landing Page</TabsTrigger>
          <TabsTrigger value="tab2">Produtos LP</TabsTrigger>
          <TabsTrigger value="tab3">Parâmetros</TabsTrigger>
          <TabsTrigger value="tab4">Simular BP</TabsTrigger>
          <TabsTrigger value="tab5">Landing Page Preview</TabsTrigger>
        </TabsList>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <TabsContent value="tab3">
              <Card className="p-4">
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <FormField
                      control={form.control}
                      name="markup_value"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel htmlFor="markup_value">
                            {"Mark-up"}
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              id="markup_value"
                              placeholder=" "
                              type="number"
                              step="0.01"
                              min="0"
                              onChange={(e) => {
                                const value = e.target.value;
                                field.onChange(
                                  value === "" ? undefined : Number(value)
                                );
                              }}
                              value={field.value || ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="commission_value"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel htmlFor="commission_value">
                            {"Comissão"}
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              id="commission_value"
                              placeholder=" "
                              type="number"
                              step="0.01"
                              min="0"
                              onChange={(e) => {
                                const value = e.target.value;
                                field.onChange(
                                  value === "" ? undefined : Number(value)
                                );
                              }}
                              value={field.value || ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="tab4">
              <Card className="p-4">
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <FormField
                      control={form.control}
                      name="conversion_rate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel htmlFor="conversion_rate">
                            {"Taxa de conversão (%)"}
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              id="conversion_rate"
                              placeholder=" "
                              type="number"
                              step="0.01"
                              min="0"
                              max="100"
                              onChange={(e) => {
                                const value = e.target.value;
                                field.onChange(
                                  value === "" ? undefined : Number(value)
                                );
                              }}
                              value={field.value || ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="tax_rate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel htmlFor="tax_rate">
                            {"Taxa de imposto (%)"}
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              id="tax_rate"
                              placeholder=" "
                              type="number"
                              step="0.01"
                              min="0"
                              max="100"
                              onChange={(e) => {
                                const value = e.target.value;
                                field.onChange(
                                  value === "" ? undefined : Number(value)
                                );
                              }}
                              value={field.value || ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="cancellation_fee"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel htmlFor="cancellation_fee">
                            {"Taxa de cancelamento"}
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              id="cancellation_fee"
                              placeholder=" "
                              type="number"
                              step="0.01"
                              min="0"
                              onChange={(e) => {
                                const value = e.target.value;
                                field.onChange(
                                  value === "" ? undefined : Number(value)
                                );
                              }}
                              value={field.value || ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="operating_expenses"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel htmlFor="operating_expenses">
                            {"Despesas operacionais (R$)"}
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              id="operating_expenses"
                              placeholder=" "
                              type="number"
                              step="0.01"
                              min="0"
                              onChange={(e) => {
                                const value = e.target.value;
                                field.onChange(
                                  value === "" ? undefined : Number(value)
                                );
                              }}
                              value={field.value || ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="customer_base"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel htmlFor="customer_base">
                            {"Base de clientes"}
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              id="customer_base"
                              placeholder=" "
                              type="number"
                              step="1"
                              min="0"
                              onChange={(e) => {
                                const value = e.target.value;
                                field.onChange(
                                  value === "" ? undefined : Number(value)
                                );
                              }}
                              value={field.value || ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="tab2">
              <Card className="p-4">
                <CardHeader>Planos</CardHeader>
                <CardContent>
                  <p className="text-gray-500">
                    {projectId 
                      ? "Selecione os planos do projeto que deseja adicionar para a landing page."
                      : "Selecione os planos que deseja adicionar para a landing page."
                    }
                  </p>
                  <p className="text-gray-500 text-sm">
                    Landing page ideal possui no máximo 3 planos.
                  </p>
                  <div>
                    {isLoadingPlans && (
                      <div className="flex items-center justify-center py-8">
                        <p className="text-muted-foreground">Carregando planos...</p>
                      </div>
                    )}
                    {!isLoadingPlans && clientPlans.length === 0 && (
                      <div className="flex items-center gap-2 mb-4">
                        <p className="text-gray-500">
                          {projectId 
                            ? "Nenhum plano encontrado neste projeto. Crie planos no projeto primeiro."
                            : "Nenhum plano adicionado"
                          }
                        </p>
                      </div>
                    )}
                    {!isLoadingPlans && clientPlans.length > 0 && (
                      <div className="space-y-4">
                        {/* Contador e Controles de Seleção */}
                        <div className="flex items-center justify-between">
                          <div className="text-sm text-muted-foreground">
                            Planos selecionados:{" "}
                            <span className="font-medium">
                              {getSelectedPlansCount()}
                            </span>{" "}
                            de {clientPlans.length}
                          </div>
                          {getSelectedPlansCount() > 0 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={clearSelectedPlans}
                            >
                              Limpar Seleção
                            </Button>
                          )}
                        </div>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Selecionar</TableHead>
                              <TableHead>Nome do Plano</TableHead>
                              <TableHead>Descrição</TableHead>
                              <TableHead>Preço</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Data de Criação</TableHead>
                              <TableHead>Ações</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {clientPlans.map((plan) => (
                              <TableRow key={plan.id}>
                                <TableCell>
                                  <Checkbox
                                    checked={isPlanSelected(plan.id)}
                                    onCheckedChange={(checked) =>
                                      togglePlanSelection(plan, checked === true)
                                    }
                                  />
                                </TableCell>
                                <TableCell className="font-medium">
                                  {plan.name}
                                </TableCell>
                                <TableCell>{plan.description}</TableCell>
                                <TableCell>
                                  {formatPrice(plan.plan_price)}
                                </TableCell>
                                <TableCell>
                                  <span
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      plan.is_active
                                        ? "bg-green-100 text-green-800"
                                        : "bg-red-100 text-red-800"
                                    }`}
                                  >
                                    {plan.is_active ? "Ativo" : "Inativo"}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  {new Date(plan.created_at).toLocaleDateString(
                                    "pt-BR"
                                  )}
                                </TableCell>
                                <TableCell>
                                  <div className="flex gap-2">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() =>
                                        openCommissionModal(plan.id)
                                      }
                                      disabled={isLoadingPlan}
                                    >
                                      {isLoadingPlan
                                        ? "Carregando..."
                                        : "Ver Carregamento"}
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => openProductsModal(plan.id)}
                                      disabled={isLoadingPlan}
                                    >
                                      Ver Produtos
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="tab1">
              <Card className="p-4">
                <Card className="p-4">
                  <CardHeader>Informações da LP</CardHeader>
                  <CardContent>
                    <div className="grid grid-cols- md:grid-cols-2 lg:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="landing_page.title"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel htmlFor="landing_page.title">
                              {"Título"}
                            </FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                id="landing_page.title"
                                placeholder=" "
                                className="w-1/2"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="landing_page.primary_button_text"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel htmlFor="landing_page.primary_button_text">
                              {"Texto do botão principal"}
                            </FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                id="landing_page.primary_button_text"
                                placeholder=" "
                                className="w-1/2"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="landing_page.description"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Descrição</FormLabel>
                            <FormControl>
                              <RichTextEditor
                                content={field.value as string}
                                onChange={field.onChange}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="landing_page.image_banner_primary"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center gap-2">
                                <Upload className="h-4 w-4 shrink-0 opacity-50" />
                                <FormLabel htmlFor="landing_page.image_banner_primary">
                                  Imagem banner principal
                                </FormLabel>
                              </div>
                              <FormControl>
                                <Input
                                  id="landing_page.image_banner_primary"
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      // Atualiza o campo do formulário
                                      field.onChange(file);
                                      // Atualiza o preview
                                      const reader = new FileReader();
                                      reader.onload = (ev) => {
                                        if (
                                          ev.target &&
                                          typeof ev.target.result === "string"
                                        ) {
                                          setPrimaryImagePreview(
                                            ev.target.result
                                          );
                                        } else {
                                          setPrimaryImagePreview(undefined);
                                        }
                                      };
                                      reader.readAsDataURL(file);
                                    } else {
                                      field.onChange(undefined);
                                      setPrimaryImagePreview(undefined);
                                    }
                                  }}
                                />
                              </FormControl>
                              {primaryImagePreview ? (
                                <img
                                  src={primaryImagePreview}
                                  alt="Preview Imagem Primária"
                                  className="mt-2 max-h-48 rounded border w-fit"
                                />
                              ) : isEditMode && editingPlatform?.landing_page?.image_banner_primary ? (
                                <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                                  <Upload className="h-4 w-4" />
                                  <span>Imagem atual: {editingPlatform.landing_page.image_banner_primary.split('/').pop()}</span>
                                </div>
                              ) : null}
                              <FormMessage />
                            </div>
                          </FormItem>
                        )}
                      />
                    </div>
                  </CardContent>
                </Card>
                <Card className="p-4">
                  <CardHeader>Informações de Contato</CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <FormField
                        control={form.control}
                        name="landing_page.whatsapp_number"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel htmlFor="landing_page.whatsapp_number">
                              {"Whatsapp"}
                            </FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                id="landing_page.whatsapp_number"
                                placeholder=" "
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="landing_page.instagram_url"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel htmlFor="landing_page.instagram_url">
                              {"Instagram URL"}
                            </FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                id="landing_page.instagram_url"
                                placeholder=" "
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="landing_page.facebook_url"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel htmlFor="landing_page.facebook_url">
                              {"Facebook URL"}
                            </FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                id="landing_page.facebook_url"
                                placeholder=" "
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="landing_page.contact_email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel htmlFor="landing_page.contact_email">
                              {"Email de contato"}
                            </FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                id="landing_page.contact_email"
                                placeholder=" "
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </CardContent>
                </Card>
                <Card className="p-4">
                  <CardHeader>Logos</CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="landing_page.primary_logo"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center gap-2">
                                <Upload className="h-4 w-4 shrink-0 opacity-50" />
                                <FormLabel htmlFor="landing_page.primary_logo">
                                  Logo Primário
                                </FormLabel>
                              </div>
                              <FormControl>
                                <Input
                                  id="landing_page.primary_logo"
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      // Atualiza o campo do formulário
                                      field.onChange(file);
                                      // Atualiza o preview
                                      const reader = new FileReader();
                                      reader.onload = (ev) => {
                                        if (
                                          ev.target &&
                                          typeof ev.target.result === "string"
                                        ) {
                                          setPrimaryLogoPreview(
                                            ev.target.result
                                          );
                                        } else {
                                          setPrimaryLogoPreview(undefined);
                                        }
                                      };
                                      reader.readAsDataURL(file);
                                    } else {
                                      field.onChange(undefined);
                                      setPrimaryLogoPreview(undefined);
                                    }
                                  }}
                                />
                              </FormControl>
                              {primaryLogoPreview ? (
                                <img
                                  src={primaryLogoPreview}
                                  alt="Preview Logo Primário"
                                  className="mt-2 max-h-48 rounded border w-fit"
                                />
                              ) : isEditMode && editingPlatform?.landing_page?.primary_logo ? (
                                <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                                  <Upload className="h-4 w-4" />
                                  <span>Logo atual: {editingPlatform.landing_page.primary_logo.split('/').pop()}</span>
                                </div>
                              ) : null}
                              <FormMessage />
                            </div>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="landing_page.secondary_logo"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center gap-2">
                                <Upload className="h-4 w-4 shrink-0 opacity-50" />
                                <FormLabel htmlFor="landing_page.secondary_logo">
                                  Logo Secundário
                                </FormLabel>
                              </div>
                              <FormControl>
                                <Input
                                  id="landing_page.secondary_logo"
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      // Atualiza o campo do formulário
                                      field.onChange(file);
                                      // Atualiza o preview
                                      const reader = new FileReader();
                                      reader.onload = (ev) => {
                                        if (
                                          ev.target &&
                                          typeof ev.target.result === "string"
                                        ) {
                                          setSecondaryLogoPreview(
                                            ev.target.result
                                          );
                                        } else {
                                          setSecondaryLogoPreview(undefined);
                                        }
                                      };
                                      reader.readAsDataURL(file);
                                    } else {
                                      field.onChange(undefined);
                                      setSecondaryLogoPreview(undefined);
                                    }
                                  }}
                                />
                              </FormControl>
                              {secondaryLogoPreview ? (
                                <img
                                  src={secondaryLogoPreview}
                                  alt="Preview Logo Secundário"
                                  className="mt-2 max-h-48 rounded border w-fit"
                                />
                              ) : isEditMode && editingPlatform?.landing_page?.secondary_logo ? (
                                <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                                  <Upload className="h-4 w-4" />
                                  <span>Logo atual: {editingPlatform.landing_page.secondary_logo.split('/').pop()}</span>
                                </div>
                              ) : null}
                              <FormMessage />
                            </div>
                          </FormItem>
                        )}
                      />
                    </div>
                  </CardContent>
                </Card>
                <Card className="p-4">
                  <CardHeader>Cores da landing page</CardHeader>
                  <CardContent>
                    <PlatformColors
                      selectedPrimaryColor={form.watch(
                        "landing_page.primary_color"
                      )}
                      selectedSecondaryColor={form.watch(
                        "landing_page.secondary_color"
                      )}
                      selectedBackgroundColor={form.watch(
                        "landing_page.background_color"
                      )}
                      onPrimaryColorChange={(color) => {
                        form.setValue("landing_page.primary_color", color);
                      }}
                      onSecondaryColorChange={(color) => {
                        form.setValue("landing_page.secondary_color", color);
                      }}
                      onBackgroundColorChange={(color) => {
                        form.setValue("landing_page.background_color", color);
                      }}
                    />
                  </CardContent>
                </Card>
                <Card className="p-4">
                  <CardHeader>Fontes da landing page</CardHeader>
                  <CardContent>
                    <FontSelector
                      selectedTitleFont={form.watch("landing_page.title_font_family")}
                      selectedBodyFont={form.watch("landing_page.body_font_family")}
                      onTitleFontChange={(font) => {
                        form.setValue("landing_page.title_font_family", font);
                      }}
                      onBodyFontChange={(font) => {
                        form.setValue("landing_page.body_font_family", font);
                      }}
                    />
                  </CardContent>
                </Card>
              </Card>
            </TabsContent>
            <TabsContent value="tab5">
              <Card className="p-4 flex flex-col gap-4">
                <CardHeader>Abaixo está o preview da landing page</CardHeader>
                <CardContent>
                  <div className="flex flex-col gap-4 w-fit">
                    <Button
                      type="button"
                      className="w-full"
                      disabled={isLoading}
                      onClick={async () => {
                        try {
                          const formData = form.getValues();

                          if (!formData.landing_page.image_banner_primary && !isEditMode) {
                            toast({
                              title: "Erro",
                              description: "Imagem do banner é obrigatória",
                              variant: "destructive",
                            });
                            return;
                          }

                          if (!formData.landing_page.primary_logo && !isEditMode) {
                            toast({
                              title: "Erro",
                              description: "Logo primário é obrigatório",
                              variant: "destructive",
                            });
                            return;
                          }

                          await onSubmit(formData);
                        } catch (error) {
                          console.error("Erro ao processar plataforma:", error);
                        }
                      }}
                    >
                      {isLoading ? "Salvando..." : isEditMode ? "Atualizar Plataforma" : "Cadastrar Plataforma"}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <LandingPagePreview
                title={form.watch("landing_page.title")}
                description={form.watch("landing_page.description")}
                primaryButtonText={form.watch(
                  "landing_page.primary_button_text"
                )}
                imageBannerPrimary={primaryImagePreview}
                primaryLogo={primaryLogoPreview}
                secondaryLogo={secondaryLogoPreview}
                whatsappNumber={form.watch("landing_page.whatsapp_number")}
                instagramUrl={form.watch("landing_page.instagram_url")}
                facebookUrl={form.watch("landing_page.facebook_url")}
                contactEmail={form.watch("landing_page.contact_email")}
                primaryColor={form.watch("landing_page.primary_color")}
                secondaryColor={form.watch("landing_page.secondary_color")}
                backgroundColor={form.watch("landing_page.background_color")}
                titleFontFamily={form.watch("landing_page.title_font_family")}
                bodyFontFamily={form.watch("landing_page.body_font_family")}
                selectedPlans={selectedPlans}
              />
            </TabsContent>

            {/* Modal de Carregamento Comercial */}
            <CommissionModal
              open={isModalOpen}
              onOpenChange={closeCommissionModal}
              planData={selectedPlan}
            />

            {/* Modal de Produtos */}
            <ProductsModal
              open={isProductsModalOpen}
              onOpenChange={closeProductsModal}
              planData={selectedPlan}
            />
          </form>
        </Form>
      </Tabs>
    </div>
  );
};
